import React, { useCallback, useRef, useState } from 'react';
import { Upload, FileText, X, Download } from 'lucide-react';
import Button from '@components/common/Button';
import { importService } from '@services';
import styles from './FileUploadZone.module.css';

interface FileUploadZoneProps {
  // Single file mode
  onFileSelect?: (file: File) => void;
  onFileRemove?: () => void;
  selectedFile?: File | null;
  // Multiple files mode
  onFilesSelect?: (files: File[]) => void;
  onFileRemoveAt?: (index: number) => void;
  selectedFiles?: File[];
  // Common props
  acceptedTypes?: string[];
  maxSizeMB?: number;
  error?: string;
  isProcessing?: boolean;
  multiple?: boolean;
  showDownloadTemplate?: boolean;
  title?: string;
  subtitle?: string;
  onValidationError?: (error: string) => void;
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onFileSelect,
  onFileRemove,
  selectedFile,
  onFilesSelect,
  onFileRemoveAt,
  selectedFiles,
  acceptedTypes = ['.csv'],
  maxSizeMB = 10,
  error,
  isProcessing = false,
  multiple = false,
  showDownloadTemplate = false,
  title,
  subtitle,
  onValidationError
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Determine mode
  const isMultipleMode = multiple || !!selectedFiles;
  const hasFiles = isMultipleMode ? selectedFiles && selectedFiles.length > 0 : !!selectedFile;

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file type
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!acceptedTypes.includes(extension)) {
        return `Invalid file type. Please upload a ${acceptedTypes.join(' or ')} file.`;
      }

      // Check file size
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > maxSizeMB) {
        return `File too large. Maximum size is ${maxSizeMB}MB.`;
      }

      return null;
    },
    [acceptedTypes, maxSizeMB]
  );

  // Helper: Validate array of files and separate valid from invalid
  const validateFilesArray = useCallback(
    (files: File[]) => {
      const validFiles: File[] = [];
      const errors: string[] = [];

      files.forEach((file) => {
        const error = validateFile(file);
        if (error) {
          errors.push(error);
        } else {
          validFiles.push(file);
        }
      });

      return { validFiles, errors };
    },
    [validateFile]
  );

  // Helper: Process validated files and call appropriate callbacks
  const handleValidatedFiles = useCallback(
    (files: File[]) => {
      if (isMultipleMode) {
        const { validFiles, errors } = validateFilesArray(files);

        if (errors.length > 0 && onValidationError) {
          onValidationError(errors.join(', '));
        }

        if (validFiles.length > 0 && onFilesSelect) {
          onFilesSelect(validFiles);
        }
        return;
      }

      // Single file mode
      const file = files[0];
      if (!file) return;

      const validationError = validateFile(file);
      if (validationError) {
        onValidationError?.(validationError);
        return;
      }

      onFileSelect?.(file);
    },
    [isMultipleMode, validateFilesArray, validateFile, onValidationError, onFilesSelect, onFileSelect]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      handleValidatedFiles(files);
    },
    [handleValidatedFiles]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;

      const fileArray = Array.from(files);
      handleValidatedFiles(fileArray);

      // Reset input value to allow selecting same file again
      e.target.value = '';
    },
    [handleValidatedFiles]
  );

  const handleClick = useCallback(() => {
    if (!isProcessing) {
      fileInputRef.current?.click();
    }
  }, [isProcessing]);

  const handleRemove = useCallback(
    (e: React.MouseEvent, index?: number) => {
      e.stopPropagation();
      if (isMultipleMode && typeof index === 'number' && onFileRemoveAt) {
        onFileRemoveAt(index);
      } else if (!isMultipleMode && onFileRemove) {
        onFileRemove();
      }
    },
    [onFileRemove, onFileRemoveAt, isMultipleMode]
  );

  const handleDownloadTemplate = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    void importService.downloadImportTemplate('contracts');
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getZoneClassName = () => {
    let className = styles.fileUploadZone;
    if (isDragging) className += ` ${styles.fileUploadZoneDragging}`;
    if (hasFiles) className += ` ${styles.fileUploadZoneHasFile}`;
    if (error) className += ` ${styles.fileUploadZoneError}`;
    return className;
  };

  // Render single file info
  const renderSingleFileInfo = () => {
    if (!selectedFile) return null;

    return (
      <div className={styles.fileUploadZoneFileInfo}>
        <span className={styles.fileUploadZoneFileName}>{selectedFile.name}</span>
        <span className={styles.fileUploadZoneFileSize}>({formatFileSize(selectedFile.size)})</span>
        <button
          className={styles.fileUploadZoneRemoveBtn}
          onClick={handleRemove}
          title="Remove file"
          disabled={isProcessing}
        >
          <X size={18} />
        </button>
      </div>
    );
  };

  // Render multiple files list
  const renderMultipleFilesList = () => {
    if (!selectedFiles || selectedFiles.length === 0) return null;

    return (
      <div className={styles.selectedFilesList} data-test-id="selected-files-list">
        {selectedFiles.map((file, index) => (
          <div key={`${file.name}-${file.size}`} className={styles.fileItem} data-test-id="file-item">
            <FileText size={16} className={styles.fileIcon} />
            <div className={styles.fileDetails}>
              <div className={styles.fileName}>{file.name}</div>
              <div className={styles.fileMeta}>{formatFileSize(file.size)}</div>
            </div>
            <button
              className={styles.fileUploadZoneRemoveBtn}
              onClick={(e) => handleRemove(e, index)}
              title="Remove file"
              disabled={isProcessing}
              data-test-id="remove-file-button"
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    );
  };

  // Render the title shown inside the clickable drop area when files are selected.
  const renderFilesTitle = () => {
    const filesCount = selectedFiles?.length || 0;
    const filePlural = filesCount > 1 ? 's' : '';
    const titleText = isMultipleMode ? `${filesCount} File${filePlural} Selected` : 'File Ready for Import';

    return (
      <div className={styles.fileUploadZoneTitle} data-test-id="file-upload-title">
        {titleText}
      </div>
    );
  };

  // File info / remove buttons render as siblings of the click-to-browse <button>
  // (stacked above it), since a <button> can't contain nested interactive children.
  const renderFilesDetails = () => (isMultipleMode ? renderMultipleFilesList() : renderSingleFileInfo());

  // Render empty state content
  const renderEmptyContent = () => {
    const defaultTitle = isMultipleMode ? 'Drop files here or click to browse' : 'Drop CSV file here';
    const displayTitle = title || defaultTitle;
    const titleText = isDragging ? 'Drop your file here' : displayTitle;

    const sizeSuffix = isMultipleMode ? ' each' : '';
    const subtitleText = subtitle || `or click to browse • Max ${maxSizeMB}MB${sizeSuffix}`;

    return (
      <>
        <div className={styles.fileUploadZoneTitle} data-test-id="file-upload-title">
          {titleText}
        </div>
        <div className={styles.fileUploadZoneSubtitle}>{subtitleText}</div>
      </>
    );
  };

  return (
    /* Drag/drop lives on the wrapper so the whole zone — including the file list
       rendered outside the button — accepts drops. The button handles click-to-browse. */
    <div
      className={getZoneClassName()}
      data-test-id="file-upload-zone"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Kept outside the button: a click() on an input nested in a button bubbles
          back to that button and re-invokes handleClick, opening the picker twice
          (and a form control inside a button is invalid HTML). */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleFileInputChange}
        multiple={isMultipleMode}
        style={{ display: 'none' }}
        data-test-id="file-upload-input"
      />

      {/* Stretched over the whole zone so click-to-browse still covers the full
          dashed area, while the file list and its remove buttons stack above it. */}
      <button
        type="button"
        className={styles.fileUploadZoneDropArea}
        onClick={handleClick}
        disabled={isProcessing}
        aria-label={title || (isMultipleMode ? 'Drop files here or click to browse' : 'Drop CSV file here')}
      />

      <div className={styles.fileUploadZoneContent}>
        <div className={styles.fileUploadZoneIcon}>{hasFiles ? <FileText size={48} /> : <Upload size={48} />}</div>

        {hasFiles ? renderFilesTitle() : renderEmptyContent()}

        {hasFiles && renderFilesDetails()}
      </div>

      {error && (
        <div
          className={styles.fileUploadZoneErrorMessage}
          style={{ color: 'var(--error-color)', marginTop: '8px' }}
          data-test-id="file-error"
        >
          {error}
        </div>
      )}

      {showDownloadTemplate && (
        <div className={styles.fileUploadZoneActions}>
          <Button variant="ghost" size="sm" onClick={handleDownloadTemplate} dataTestId="download-template-button">
            <Download size={16} />
            Download Template
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;
